import {
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  FormControlLabel,
} from "@material-ui/core"
import Autocomplete from "@material-ui/lab/Autocomplete"
import { Form, Formik } from "formik"
import { useContext, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"
import * as Yup from "yup"
import { listClients } from "~/actions/clients"
import { createAnalyticsUser, updateAnalyticsUser } from "~/actions/users"
import LoaderButton from "~/components/LoaderButton/index.jsx"
import AppContext from "~/contexts/App"

import Box from "~/components/Common/Box"
import useStyles from "./styles"

const schema = Yup.object().shape({
  id: Yup.number().nullable(),
  name: Yup.string().required("Please enter a full name"),
  email: Yup.string().email().required("Please enter a valid email"),
  clients: Yup.array().test({
    message: "Select a client",
    test: (arr) => arr.length !== 0,
  }),
  metrics: Yup.boolean(),
})

const CreateAdminDialog = () => {
  const classes = useStyles()
  const { dialogs, closeDialog } = useContext(AppContext)
  const { authUser, openSnackBar } = useContext(AppContext)
  const queryClient = useQueryClient()
  const thisDialog = dialogs?.["createAnalyticsUserDialog"] || {}
  const { open = false, data } = thisDialog
  let userData = thisDialog.data
  const createAnalyticsUserMutation = useMutation(createAnalyticsUser)
  const updateAnalyticsUserMutation = useMutation(updateAnalyticsUser)
  const [clientSelectVal, setClientSelectVal] = useState("")
  const [metrics, setMetrics] = useState(false)
  const handleClose = () => {
    closeDialog("createAnalyticsUserDialog", false)
  }

  const onSubmit = (values, actions) => {
    const clientIds = values?.clients?.map((c) => c.id)
    if (values.id) {
      // Edit
      let data = {
        name: values?.name,
        email: values?.email,
        id: values?.id,
        clients: clientIds,
      }
      updateAnalyticsUserMutation
        ?.mutateAsync(data)
        .then((res) => {
          queryClient.setQueryData("analytics_users", (oldData) => {
            let newData = oldData.map((x) => {
              if (Number(x.id) === Number(res.id)) {
                return res
              } else {
                return x
              }
            })
            return newData
          })
          openSnackBar({ message: "Analytics User Updated" })
          handleClose()
        })
        .catch((err) => {
          console.error(err)
          openSnackBar({ message: "Error Updating Analytics User" })
          handleClose()
        })
    } else {
      // Create
      let data = {
        name: values?.name,
        email: values?.email,
        clients: clientIds,
        metrics: metrics,
      }
      createAnalyticsUserMutation
        ?.mutateAsync(data)
        .then((res) => {
          queryClient.setQueryData("analytics_users", (oldData) => {
            return [...oldData, res]
          })
          openSnackBar({ message: "Analytics User Invited" })
          handleClose()
        })
        .catch((err) => {
          console.error(err)
          openSnackBar({ message: "Error Inviting Analytics User" })
          handleClose()
        })
    }
  }

  const {
    data: clients,
    refetch: refetchClients,
    isLoading: loadingClients,
  } = useQuery("clients", () => listClients(), { enabled: !!authUser })

  return (
    <Dialog classes={{ paperWidthSm: classes.dialogMinHeight }} open={open}>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography variant="h2" className={classes.title}>
          {userData?.id ? "Edit Analytics User" : "New Analytics User"}
        </Typography>
        <Typography color="primary" variant="subtitle2">
          {userData?.id ? "" : "   Add analytics user to a client"}
        </Typography>
      </DialogTitle>
      <Formik
        initialValues={{
          id: userData?.id || null,
          name: userData?.name || "",
          email: userData?.email || "",
          clients: userData?.clients || [],
        }}
        onSubmit={onSubmit}
        validationSchema={schema}
      >
        {({
          handleChange,
          values,
          errors,
          touched,
          handleBlur,
          isSubmitting,
          handleSubmit,
          setFieldValue,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              {loadingClients ? (
                <CircularProgress />
              ) : (
                <DialogContent className={classes.dialogContent}>
                  <Typography color="secondary" className={classes.title}>
                    Full Name
                  </Typography>

                  <TextField
                    type="text"
                    name="name"
                    id="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={
                      <span>
                        {touched.name && errors.name ? errors.name : " "}
                      </span>
                    }
                    InputProps={{
                      classes: {
                        root: classes.inputBackgroundColor,
                      },
                    }}
                    error={!!touched.name && !!errors.name}
                    variant="filled"
                    size="small"
                    fullWidth
                    FormHelperTextProps={{
                      className: classes.formHelperText,
                    }}
                  />
                  <Typography color="secondary" className={classes.title}>
                    Email
                  </Typography>

                  <TextField
                    type="text"
                    name="email"
                    id="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={
                      <span>
                        {touched.email && errors.email ? errors.email : " "}
                      </span>
                    }
                    InputProps={{
                      classes: {
                        root: classes.inputBackgroundColor,
                      },
                    }}
                    error={touched.name && errors.email}
                    variant="filled"
                    size="small"
                    fullWidth
                    FormHelperTextProps={{
                      className: classes.formHelperText,
                    }}
                  />
                  <Typography color="secondary" className={classes.title}>
                    Client
                  </Typography>
                  <Autocomplete
                    size="small"
                    value={clientSelectVal.name || ""}
                    options={clients || []}
                    getOptionLabel={(option) => option?.name || ""}
                    getOptionDisabled={(option) =>
                      !!values.clients?.find((x) => x.name === option.name)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        value={clientSelectVal}
                        placeholder="Add Clients (auto complete)"
                        variant="filled"
                        error={touched.clients && errors.clients}
                        helperText={
                          <span>
                            {touched.clients && errors.clients
                              ? errors.clients
                              : " "}
                          </span>
                        }
                      />
                    )}
                    onChange={(_e, val) => {
                      setFieldValue("clients", [...values?.clients, val])
                      setClientSelectVal(val)
                    }}
                    openOnFocus
                    disableClearable
                    classes={{
                      inputRoot: classes.autocompleteRoot,
                    }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={metrics}
                        name="application"
                        onChange={() => setMetrics(!metrics)}
                      />
                    }
                    label="Invite to metrics.dataprotocol.com?"
                  />

                  <Box
                    marginTop="8px"
                    marginBottom="16px"
                    display="flex"
                    flexWrap="wrap"
                  >
                    {values?.clients?.map((client, i) => {
                      return (
                        <Chip
                          key={i}
                          label={client.name}
                          size="small"
                          color="primary"
                          onDelete={() =>
                            setFieldValue(
                              "clients",
                              values?.clients?.filter(
                                (x) => x?.name !== client?.name,
                              ),
                            )
                          }
                          style={{ marginRight: "8px", marginTop: "8px" }}
                        />
                      )
                    })}
                  </Box>
                </DialogContent>
              )}
              <DialogActions>
                <Button onClick={handleClose} style={{ height: "40px" }}>
                  Cancel
                </Button>
                <LoaderButton
                  type="submit"
                  // onClick={onSubmit}
                  classoverrides={{
                    wrapper: classes.button,
                    button: {
                      root: classes.buttonRoot,
                    },
                  }}
                  variant="contained"
                  color="secondary"
                  working={isSubmitting}
                  size="small"
                >
                  {values.id !== null ? "SAVE" : "INVITE"}
                </LoaderButton>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}

export default CreateAdminDialog
